import { useState, useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import classNames from 'classnames'
import loadable from '@loadable/component'
import Tooltip from 'components/misc/Tooltip'
import Header from 'components/typography/Header'

import CircleSpinner from 'components/misc/CircleSpinner'
import { Fade, FadeVisual } from 'components/misc/Fade'
import PaperCard from 'components/report/PaperCard'
import Paragraph from 'components/typography/Paragraph'
import { userIsAtLeast } from 'lib/user'
import Button from 'components/forms/Button'
import { Link } from 'components/misc/Link'
import Notification from 'components/misc/Notification'
import { selectVisibleCitationsByPaper } from 'store/report/selectors'
import useScroll from 'hooks/useScroll'
import { PLANS } from 'server/lib/constants'

import styles from './styles/PapersList.css'

const PaywallBanner = loadable(() => import('components/misc/PaywallBanner'))

//
// Avoid rendering PaywallBanner until we need to show it
// also make sure the component is loaded with show={false}
// when we show it to avoid it fades in
//
const PaywalledCites = ({ hasScrolled, user, textMatch, citations, children }) => {
  const [loaded, setLoaded] = useState(false)
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (hasScrolled) {
      //
      // potentially fallback to this
      // if promise below never finishes
      //
      setTimeout(() => {
        setLoaded(true)
      }, 3000)

      PaywallBanner.load().then(() => {
        setLoaded(true)
      })
    }
  }, [hasScrolled, setLoaded])

  useEffect(() => {
    if (loaded) {
      setTimeout(() => setShow(true), 500)
    }
  }, [loaded, setShow])

  if (loaded) {
    return (
      <PaywallBanner
        show={show}
        paywallSource='Reports'
        freeHeader='Create an account to read the remaining citation statements from this report. You will also get access to:'
        upgradeHeader={userIsAtLeast(user, PLANS.premium) ? "You've reached your monthly limit. Please upgrade to get unlimited access to:" : 'Please upgrade to get access to:'}
      >
        {children}
      </PaywallBanner>
    )
  }

  return (
    <div className={styles.paywalledCites}>
      {children}
    </div>
  )
}

export const PapersList = ({ paywall }) => {
  const visibleCitationsByPaper = useSelector(({ report }) => selectVisibleCitationsByPaper(report))
  const textMatch = useSelector(({ report: { filters: { textMatch } } }) => textMatch)
  const sorting = useSelector(({ report: { sorting } }) => sorting)
  const citationsLoadTracker = useSelector(({ report: { citationsLoadTracker } }) => citationsLoadTracker)
  const showNoCites = visibleCitationsByPaper.length === 0 && citationsLoadTracker.state !== 'loading' && !sorting
  const doi = useSelector(({ report }) => report?.paper)
  const title = useSelector(({ report }) => report?.papers[report?.paper]?.title)
  const [hasScrolled, setHasScrolled] = useState(false)

  const handleScroll = useCallback(({ lastKnownY }) => {
    if (hasScrolled || lastKnownY < 10) {
      return
    }

    setTimeout(() => setHasScrolled(true), 200)
  }, [hasScrolled, setHasScrolled])

  useScroll(handleScroll)

  return (
    <div
      className={classNames(styles.citationList, {
        relative: sorting
      })}
    >
      <FadeVisual show={!sorting}>
        {paywall && !showNoCites
          ? (
            <>
              <PaperCard
                paperIndex={0}
                textMatch={textMatch}
                citations={visibleCitationsByPaper[0]?.citations}
                paper={visibleCitationsByPaper[0]?.paper}
                tally={visibleCitationsByPaper[0]?.tally}
                restricted={paywall}
                showAbstract={false}
              />
              <PaywalledCites hasScrolled={hasScrolled}>
                <div className={styles.paperCardsLayout}>
                  {
                    visibleCitationsByPaper.slice(0, 3).map((paperWithCitations, index) => (
                      <PaperCard
                        textMatch={textMatch}
                        key={`${index}-${paperWithCitations?.paper?.doi}`}
                        paperIndex={index}
                        citations={paperWithCitations?.citations}
                        paper={paperWithCitations?.paper}
                        tally={paperWithCitations?.tally}
                        showAbstract={false}
                      />
                    ))
                  }
                </div>
              </PaywalledCites>
            </>
            )
          : (
              visibleCitationsByPaper.map((paperWithCitations, index) => (
                <PaperCard
                  textMatch={textMatch}
                  key={`${index}-${paperWithCitations?.paper?.doi}`}
                  paperIndex={index}
                  citations={paperWithCitations?.citations}
                  paper={paperWithCitations?.paper}
                  tally={paperWithCitations?.tally}
                  showAbstract={false}
                />
              ))
            )}
      </FadeVisual>
      <Fade className={styles.noCites} show={showNoCites}>
        <Header level={4} className={styles.inlineBlock}>No citations</Header>
        <div className={styles.inlineBlock}>
          <Tooltip
            className={styles.countDisclaimer}
            refClassName={styles.noCitesDisclaimerIconWrapper}
            header='Not what you expected?'
            content={[
              "In order to extract citation statements, we need to process full-text articles whereas other citation indices do not. While this means our numbers might be a bit lower, we're adding millions of citation statements to the database each day!",
              <Paragraph key='alert'>Click set alert to get an email when it receives citations.</Paragraph>
            ]}
          >
            <i className={styles.countDisclaimerIcon} />
          </Tooltip>
        </div>
        <div className={styles.notificationContainer}> Set email alert for when this publication receives citations?
          <Notification
            source={doi}
            name={title || 'untitled'}
            category='paper'
            className={styles.notification}
            checkbox
          />
        </div>
        <Paragraph>
          <Link to={`/search/citations?q=${encodeURIComponent(title)}`}>
            <Button color='blue' className={styles.searchButton}>
              Search citation statements from papers discussing a similar topic
            </Button>
          </Link>
        </Paragraph>
      </Fade>
      <FadeVisual className={styles.loading} show={sorting}>
        <CircleSpinner />
      </FadeVisual>
    </div>
  )
}

export default PapersList
